/**
 * The rooms ("Räumlichkeiten") a Franchise-Partner authors for their Standort
 * — spec 11.02.07-Rooms.
 *
 * Shared because the portal enforces the same limits client-side: the upstream
 * SH Franchise Dashboard API answers a violation with a bare `409`/`400`, which
 * says nothing a partner could act on.
 */

/**
 * franchise-dashboard-api.md § Create rooms: "Up to nine rooms per website data
 * record." Enforced upstream too — checked here so a partner is told before the
 * write leaves, and so the portal can disable "Add" instead of failing on click.
 */
export const MAX_ROOMS_PER_LOCATION = 9;

/** franchise-dashboard-api.md § Create rooms — `title` maximum length. */
export const MAX_ROOM_TITLE_LENGTH = 255;

/**
 * franchise-dashboard-api.md § Create/Update website data — `rooms_headline`
 * maximum length.
 */
export const MAX_ROOMS_HEADLINE_LENGTH = 255;

/**
 * What a room's description is sent upstream as when the partner leaves it
 * empty.
 *
 * Schülerhilfe requires a non-empty `description` on every room and answers a
 * blank one with a bare `400`. The field is not worth forcing on a partner —
 * nothing renders it (spec 10.03.02.01.19-Location-Facility-Gallery), so an
 * empty one costs the visitor nothing — hence a placeholder rather than a
 * required field.
 *
 * The backend substitutes it on the way out, and the portal maps it back to an
 * empty field on the way in, so a partner never sees a dash they did not type.
 * A partner who really does type `-` gets the same round trip and the same
 * stored value, which is why this needs no marker beyond the character itself.
 */
export const ROOM_DESCRIPTION_PLACEHOLDER = '-';

/**
 * What a room's title is sent upstream as when the partner leaves it empty.
 *
 * Same bargain as `ROOM_DESCRIPTION_PLACEHOLDER`, for the same reason: SH
 * requires a non-empty `title` and answers a blank one with a bare `400`, but
 * a room is perfectly identifiable by its photo alone, so the portal lets the
 * partner skip it and the backend fills in a dash on the way out.
 *
 * The portal maps it back to an empty field on the way in, and the website
 * declines to render it — see `hasDisplayableRoomTitle`.
 */
export const ROOM_TITLE_PLACEHOLDER = '-';

/**
 * Whether a room's title is worth putting on the Standort page.
 *
 * A caption that reads "-" is worse than no caption: it looks like the page
 * failed rather than like a room the partner chose not to name. This is the
 * read-side counterpart to `ROOM_TITLE_PLACEHOLDER` — but deliberately wider
 * than `title === '-'`, because rooms created before the title was optional
 * carry whatever single character a partner typed to get past the old required
 * field — a `.` or a `1` says no more than the dash does.
 *
 * The two bars are an **AND**: a title is hidden only when it is *both*
 * shorter than two characters *and* starts with something outside `[a-zA-Z]`.
 * Either alone would be far too eager — `Ärztezimmer`, `Überaum` and `1. OG`
 * are real room names whose first character is not an ASCII letter, and `A`
 * is a real (if terse) one that is a single character.
 */
export function hasDisplayableRoomTitle(
  title: string | null | undefined,
): boolean {
  const trimmed = title?.trim() ?? '';
  return trimmed.length >= 2 || /^[a-zA-Z]/.test(trimmed);
}
